import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi, Outlet, useParams } from '@tanstack/react-router';
import type { ApprovalSummary } from '@app/shared';
import {
  type Column,
  Cluster,
  DataTable,
  EmptyState,
  Icons,
  PageHeader,
  SplitView,
  StatusBadge,
  Stack,
  Text,
  useIsCompact,
} from '../ui';
import { api } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useWorkspaceId } from '../lib/auth';
import { useOperationsStream } from '../lib/useOperationsStream';
import { isSendClassTool } from '../lib/sendClass';
import { toolLabel } from '../lib/toolLabels';
import { durationMs, formatDuration } from '../lib/duration';
import { RunStatusBadge } from '../components/RunStatusBadge';

const approvalsRoute = getRouteApi('/app/approvals');

/** Higher-risk approvals float to the top; within a tier, the oldest (closest to expiry) first. */
const RISK_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

/** "How long this action has been waiting" — a plain now − createdAt at render (no ticker). */
function ageLabel(createdAtIso: string): string {
  return formatDuration(durationMs(createdAtIso, new Date().toISOString())) ?? 'just now';
}

/**
 * Pending-approvals inbox (§14), redesigned as the master pane of a SPLIT VIEW that
 * mirrors the Runs screen (Phase 3a) so the two review loops feel identical. The
 * prioritized list of pending approvals is the master; selecting a row deep-links to
 * `/app/approvals/$approvalId` (rendered in the detail pane via `<Outlet/>`) — the
 * trust-critical review surface — without losing the inbox. On desktop the list fills
 * the width while browsing, then collapses beside the detail once an approval is open;
 * on mobile it is list → detail navigation. Live via SSE.
 */
export function ApprovalsInboxPage() {
  const workspaceId = useWorkspaceId();
  useOperationsStream(workspaceId);

  const navigate = approvalsRoute.useNavigate();
  const { approvalId: selectedApprovalId } = useParams({ strict: false }) as { approvalId?: string };
  const compact = useIsCompact();

  const approvals = useQuery({
    queryKey: qk.approvals(workspaceId),
    queryFn: () => api.listApprovals(workspaceId, { status: 'pending', pageSize: 100 }),
  });

  const rows = useMemo(() => {
    const data = approvals.data?.data ?? [];
    return [...data].sort((a, b) => {
      const risk = (RISK_ORDER[a.risk] ?? 3) - (RISK_ORDER[b.risk] ?? 3);
      if (risk !== 0) return risk;
      return Date.parse(a.createdAt) - Date.parse(b.createdAt);
    });
  }, [approvals.data]);

  const openApproval = (a: ApprovalSummary): void =>
    void navigate({ to: '/app/approvals/$approvalId', params: { approvalId: a.id } });
  const clearSelection = (): void => void navigate({ to: '/app/approvals' });

  const columns: Column<ApprovalSummary>[] = [
    {
      id: 'request',
      header: 'Proposed action',
      hideCardLabel: true,
      cell: (a) => (
        <Stack gap="1">
          <Cluster gap="2" wrap>
            <Text as="span" weight="semibold" truncate title={toolLabel(a.tool)}>
              {toolLabel(a.tool)}
            </Text>
            {/* The risk badge alone carries the alarm (red = high risk); the "will send"
                qualifier is a calm, neutral pill so red means exactly one thing. */}
            {isSendClassTool(a.tool) && (
              <StatusBadge tone="neutral" size="sm" icon={Icons.Send} label="will send" />
            )}
          </Cluster>
          <Text as="span" size="xs" mono tone="muted" truncate title={a.tool}>
            {a.tool}
          </Text>
          <Text size="sm" tone="secondary" truncate>
            {a.reason || 'No reason given.'}
          </Text>
        </Stack>
      ),
    },
    {
      id: 'risk',
      header: 'Risk',
      width: '8rem',
      cardLabel: 'Risk',
      hideCardLabel: true,
      cell: (a) => <RunStatusBadge status={a.risk} size="sm" />,
    },
    {
      id: 'age',
      header: 'Waiting',
      align: 'end',
      width: '12rem',
      cardLabel: 'Waiting',
      cell: (a) => (
        <Stack gap="1" align="end">
          <Text as="span" size="sm" weight="medium">
            {ageLabel(a.createdAt)}
          </Text>
          <Text as="span" size="xs" tone="muted">
            expires {new Date(a.expiresAt).toLocaleString()}
          </Text>
        </Stack>
      ),
    },
  ];

  const emptyState = (
    <EmptyState
      icon={Icons.ShieldCheck}
      title="No approvals waiting"
      description="When an agent proposes a high-stakes action (like sending an email), it pauses here for your review."
    />
  );

  const list = (
    <DataTable
      caption="Pending approvals in this workspace"
      columns={columns}
      rows={rows}
      getRowId={(a) => a.id}
      activeRowId={selectedApprovalId}
      onRowClick={openApproval}
      rowTestId="approval-row"
      loading={approvals.isLoading}
      error={approvals.isError}
      onRetry={() => void approvals.refetch()}
      isEmpty={rows.length === 0}
      emptyState={emptyState}
      layout={selectedApprovalId ? 'cards' : 'auto'}
    />
  );

  // On mobile the page chrome (header) belongs to the list view; when an approval is
  // open the detail takes over full-screen, so hide the chrome then.
  const showChrome = !(compact && !!selectedApprovalId);

  return (
    <Stack gap="6">
      {showChrome && (
        <PageHeader
          title="Approvals"
          description="Actions awaiting your review — the platform pauses here before it acts on your behalf."
        />
      )}

      <SplitView
        list={list}
        detail={<Outlet />}
        hasSelection={!!selectedApprovalId}
        onBack={clearSelection}
        backLabel="All approvals"
        listWidth="28rem"
        listLabel="Approvals"
        detailLabel="Approval detail"
      />
    </Stack>
  );
}
